import { useEffect, useRef, useState } from 'react';

import { animateScroll } from '../../utils/animateScroll/animateScroll';

type UseScrollableTabsReturn = {
  scrollerRef: React.RefObject<HTMLDivElement | null>;
  tabListRef: React.RefObject<HTMLDivElement | null>;
  displayStartScroll: boolean;
  displayEndScroll: boolean;
  handleStartScrollClick: () => void;
  handleEndScrollClick: () => void;
};

export function useScrollableTabs(): UseScrollableTabsReturn {
  const scrollerRef = useRef<HTMLDivElement>(null);
  const tabListRef = useRef<HTMLDivElement>(null);

  const [displayStartScroll, setDisplayStartScroll] = useState(false);
  const [displayEndScroll, setDisplayEndScroll] = useState(false);

  const scroll = (scrollValue: number) => {
    if (!scrollerRef.current) return;
    animateScroll({
      property: 'scrollLeft',
      element: scrollerRef.current,
      to: scrollValue,
      options: { duration: 300 },
    });
  };

  const getScrollSize = () => {
    const scroller = scrollerRef.current;
    const tabList = tabListRef.current;
    if (!scroller || !tabList) return 0;

    const containerSize = scroller.clientWidth;
    const children = Array.from(tabList.children) as HTMLElement[];

    return children.reduce((totalSize, acc, index) => {
      if (totalSize + acc.clientWidth > containerSize) {
        return index === 0 ? containerSize : totalSize;
      }
      return totalSize + acc.clientWidth;
    }, 0);
  };

  const moveTabsScroll = (delta: number) => {
    if (!scrollerRef.current) return;
    scroll(scrollerRef.current.scrollLeft + delta);
  };

  const handleStartScrollClick = () => {
    moveTabsScroll(-1 * getScrollSize());
  };

  const handleEndScrollClick = () => {
    moveTabsScroll(getScrollSize());
  };

  useEffect(() => {
    const scroller = scrollerRef.current;
    const tabList = tabListRef.current;
    if (!scroller || !tabList) return;

    const updateScrollButtons = () => {
      setDisplayStartScroll(scroller.scrollLeft > 0);
      setDisplayEndScroll(
        scroller.scrollLeft < scroller.scrollWidth - scroller.clientWidth - 1,
      );
    };

    scroller.addEventListener('scroll', updateScrollButtons);

    const resizeObserver = new ResizeObserver(updateScrollButtons);
    resizeObserver.observe(scroller);
    resizeObserver.observe(tabList);

    updateScrollButtons();

    return () => {
      scroller.removeEventListener('scroll', updateScrollButtons);
      resizeObserver.disconnect();
    };
  }, []);

  return {
    scrollerRef,
    tabListRef,
    displayStartScroll,
    displayEndScroll,
    handleStartScrollClick,
    handleEndScrollClick,
  };
}
